import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useTenant } from '@/hooks/useTenant';
import { normalizeCurrency } from '@/lib/currency';

export interface CartProduct {
  id: number;
  name: string;
  price: number;
  original_price: number | null;
  media: string;
  stock: number;
  /** 'physical' (défaut) ou 'digital' (livraison d'un fichier, sans stock). */
  product_type?: string;
  /** Fichier mis à disposition après l'achat (produit digital). */
  digital_file_url?: string | null;
  /** Marchand vendeur (rempli quand l'article vient du chat IA) — sert à créer la commande. */
  owner_id?: number;
  owner_name?: string;
  /** Devise du produit (la monnaie du marchand/produit reste la référence). */
  currency?: string;
}

export interface CartItem {
  product: CartProduct;
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  count: number;
  subtotal: number;
  currency: string;
  addToCart: (product: CartProduct, quantity?: number) => void;
  removeFromCart: (id: number) => void;
  updateQuantity: (id: number, delta: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | null>(null);

const STORAGE_KEY = 'zifek_cart';

/**
 * Panier de la boutique publique. Le panier est stocké côté navigateur
 * (localStorage) pour permettre un achat sans compte ; la commande finale est
 * ensuite persistée en base dans la table `commande`.
 */
export function CartProvider({ children }: { children: ReactNode }) {
  const { tenant } = useTenant();
  const currency = normalizeCurrency(tenant?.monaie || 'MAD');
  const [items, setItems] = useState<CartItem[]>([]);
  const [hydrated, setHydrated] = useState(false);

  // Chargement du panier au montage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setItems(parsed as CartItem[]);
      }
    } catch {
      /* panier corrompu : on repart de zéro */
    }
    setHydrated(true);
  }, []);

  // Persistance à chaque modification
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* stockage indisponible : on ignore */
    }
  }, [items, hydrated]);

  const addToCart = useCallback((product: CartProduct, quantity = 1) => {
    const add = Math.max(1, Math.floor(quantity || 1));
    setItems((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      const max = product.stock > 0 ? product.stock : add;
      if (existing) {
        const nextQty = Math.min(existing.quantity + add, max);
        return prev.map((i) =>
          i.product.id === product.id ? { ...i, quantity: nextQty } : i
        );
      }
      return [...prev, { product, quantity: Math.min(add, max) }];
    });
  }, []);

  const removeFromCart = useCallback((id: number) => {
    setItems((prev) => prev.filter((i) => i.product.id !== id));
  }, []);

  const updateQuantity = useCallback((id: number, delta: number) => {
    setItems((prev) =>
      prev
        .map((i) => {
          if (i.product.id !== id) return i;
          const qty = i.quantity + delta;
          if (qty <= 0) return null;
          // Un stock à 0 (article ajouté depuis le chat IA, ou produit sans
          // gestion de stock) signifie « quantité libre » : on ne bloque pas.
          if (i.product.stock > 0 && qty > i.product.stock) return i;
          return { ...i, quantity: qty };
        })
        .filter((i): i is CartItem => i !== null)
    );
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.product.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        count,
        subtotal,
        currency,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextType {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
}